// composables/useEntityLabels.ts
import { ref, computed, reactive, watch, unref, type Ref, type MaybeRef, isRef } from 'vue'
import { CesiumService } from '@/handlers/cesium.service'
import { Cartesian3, SceneTransforms, defined, type Event, type Entity } from 'cesium'

// Default maximum distance (meters) at which labels are visible
const DEFAULT_MAX_DISTANCE = 5000 // 5km
// Distance at which labels start scaling down (as fraction of maxDistance)
const SCALE_START_FRACTION = 0.3 // Start scaling at 30% of maxDistance
// Minimum scale before label disappears
const MIN_SCALE = 0.5

/**
 * Convert a `#rrggbb` (or `#rgb`) hex string into an `rgba()` with the given
 * alpha. Used for rendering tinted accents in the label DOM. Falls back to the
 * raw value when the input isn't a parseable hex (e.g. already an `rgb(...)`).
 */
const hexWithAlpha = (hex: string, alpha: number): string => {
  if (!hex || typeof hex !== 'string') return `rgba(27,146,166,${alpha})`
  let h = hex.trim().replace(/^#/, '')
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  }
  if (!/^[0-9A-Fa-f]{6}$/.test(h)) return hex
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

export interface LabelOptions {
  title?: string
  description?: string // Renders as a paragraph
  fields?: Array<{ label: string; value: string | number }>
  color?: string
  footer?: string
  offset?: [number, number]
  className?: string
  maxDistance?: number
  icon?: string
  badge?: string
}

export type LabelOptionsGetter = LabelOptions | (() => LabelOptions) | Ref<LabelOptions>

export type PositionGetter =
  | Cartesian3
  | (() => Cartesian3 | undefined)
  | Ref<Cartesian3 | undefined>

interface LabelInstance {
  element: HTMLElement
  position: Cartesian3 | (() => Cartesian3 | undefined)
  optionsGetter?: () => LabelOptions
  lastOptionsHash?: string
  visible: boolean
  maxDistance?: number
  watchers?: Array<() => void> // Store cleanup functions for watchers
}

export const useEntityLabels = () => {
  const viewer = computed(() => CesiumService.viewer.value)

  // Single state object for all labels
  const labels = reactive<Record<string, LabelInstance>>({})

  // Single container and listener
  let container: HTMLElement | null = null
  let updateListener: Event.RemoveCallback | null = null

  /**
   * Initialize or get the container and start update loop
   */
  const initSystem = () => {
    if (!viewer.value) return false

    if (!container) {
      container = document.createElement('div')
      container.id = 'entity-labels'
      container.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        overflow: hidden;
        z-index: 1;
      `
      viewer.value.container.appendChild(container)
    }

    if (!updateListener) {
      updateListener = viewer.value.scene.preRender.addEventListener(updateAllLabels)
    }

    return true
  }

  /**
   * Update all label positions and content
   */
  const updateAllLabels = () => {
    if (!viewer.value) return

    const cameraPosition = viewer.value.camera.positionWC

    for (const [id, label] of Object.entries(labels)) {
      // Update content if dynamic (also updates maxDistance)
      if (label.optionsGetter) {
        const options = label.optionsGetter()
        const hash = JSON.stringify(options)
        if (hash !== label.lastOptionsHash) {
          label.lastOptionsHash = hash
          label.maxDistance = options.maxDistance ?? DEFAULT_MAX_DISTANCE
          updateLabelDOM(label.element, options)
        }
      }

      // Update position
      const pos = typeof label.position === 'function' ? label.position() : label.position

      if (!pos || !label.visible) {
        label.element.style.display = 'none'
        continue
      }

      // Check distance-based visibility and scaling
      const maxDist = label.maxDistance || DEFAULT_MAX_DISTANCE
      const distance = Cartesian3.distance(cameraPosition, pos)

      if (distance > maxDist) {
        label.element.style.display = 'none'
        continue
      }

      // Calculate scale based on distance
      const scaleStartDist = maxDist * SCALE_START_FRACTION
      let scale = 1.0
      let opacity = 1.0

      if (distance > scaleStartDist) {
        // Interpolate scale from 1.0 to MIN_SCALE as distance goes from scaleStartDist to maxDist
        const t = (distance - scaleStartDist) / (maxDist - scaleStartDist)
        scale = 1.0 - t * (1.0 - MIN_SCALE)
        // Also fade opacity slightly
        opacity = 1.0 - t * 0.3
      }

      const screenPos = SceneTransforms.worldToWindowCoordinates(viewer.value.scene, pos)

      if (defined(screenPos)) {
        const offset = label.element.dataset
        label.element.style.left = `${screenPos.x + Number(offset.offsetX || 20)}px`
        label.element.style.top = `${screenPos.y + Number(offset.offsetY || -40)}px`
        label.element.style.transform = `translateY(-50%) scale(${scale})`
        label.element.style.opacity = String(opacity)
        label.element.style.display = 'block'
      } else {
        label.element.style.display = 'none'
      }
    }
  }

  /**
   * Create label DOM element
   */
  const createLabelDOM = (id: string, options: LabelOptions): HTMLElement => {
    const wrapper = document.createElement('div')
    wrapper.className = options.className || 'entity-label'
    wrapper.dataset.labelId = id
    wrapper.dataset.offsetX = String(options.offset?.[0] || 20)
    wrapper.dataset.offsetY = String(options.offset?.[1] || -40)
    wrapper.style.cssText = `
      position: absolute;
      transform: translateY(-50%);
      transform-origin: left center;
      transition: transform 0.15s ease-out, opacity 0.15s ease-out;
      z-index: 10;
    `

    const label = document.createElement('div')
    label.style.cssText = `
      background: linear-gradient(135deg, rgba(30,35,41,0.95) 0%, rgba(20,23,27,0.95) 100%);
      border: 1px solid rgba(255,255,255,0.1);
      border-left: 3px solid ${options.color || '#1b92a6'};
      border-radius: 8px;
      padding: 10px 12px;
      min-width: 150px;
      max-width: 220px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.4);
      font-family: system-ui, -apple-system, sans-serif;
      pointer-events: auto;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
      box-sizing: border-box;
    `

    // Add hover effect
    label.onmouseenter = () => {
      label.style.transform = 'scale(1.02)'
      label.style.boxShadow = '0 12px 32px rgba(0,0,0,0.5)'
    }
    label.onmouseleave = () => {
      label.style.transform = 'scale(1)'
      label.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)'
    }

    wrapper.appendChild(label)
    updateLabelDOM(wrapper, options)

    return wrapper
  }

  /**
   * Update label content
   */
  const updateLabelDOM = (wrapper: HTMLElement, options: LabelOptions) => {
    const label = wrapper.firstElementChild as HTMLElement
    if (!label) return

    const accent = options.color || '#1b92a6'
    const hasIcon = Boolean(options.icon)

    label.innerHTML = ''
    label.style.borderLeftColor = accent
    // Iconified labels get a slightly thicker accent and a subtle top accent
    // tint so they read as a different "kind" of label vs plain notes.
    label.style.borderLeftWidth = hasIcon ? '4px' : '3px'
    label.style.borderTop = hasIcon
      ? `1px solid ${hexWithAlpha(accent, 0.35)}`
      : '1px solid rgba(255,255,255,0.1)'
    label.style.borderRight = '1px solid rgba(255,255,255,0.1)'
    label.style.borderBottom = '1px solid rgba(255,255,255,0.1)'

    // Header
    if (options.title || hasIcon) {
      const hasContent = options.description || options.fields?.length
      const header = document.createElement('div')
      header.style.cssText = `display:flex;align-items:center;gap:${hasIcon ? '8px' : '6px'};${hasContent ? 'margin-bottom:8px;' : ''}`

      if (hasIcon) {
        const chip = document.createElement('div')
        chip.style.cssText = `
          width: 26px;
          height: 26px;
          border-radius: 6px;
          background: ${hexWithAlpha(accent, 0.18)};
          border: 1px solid ${hexWithAlpha(accent, 0.55)};
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        `
        const img = document.createElement('img')
        img.src = options.icon as string
        img.alt = ''
        img.style.cssText = 'width:16px;height:16px;display:block;pointer-events:none;'
        chip.appendChild(img)
        header.appendChild(chip)
      } else {
        const dot = document.createElement('div')
        dot.style.cssText = `width:8px;height:8px;border-radius:50%;background:${accent};flex-shrink:0;`
        header.appendChild(dot)
      }

      const titleWrap = document.createElement('div')
      titleWrap.style.cssText = 'display:flex;flex-direction:column;gap:2px;min-width:0;flex:1;'

      if (options.title) {
        const title = document.createElement('span')
        title.style.cssText = `font-size:${hasIcon ? '13px' : '12px'};font-weight:${hasIcon ? '700' : '600'};color:#e4e4e4;line-height:1.2;`
        title.textContent = options.title
        titleWrap.appendChild(title)
      }

      if (options.badge) {
        const badge = document.createElement('span')
        badge.style.cssText = `
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: ${accent};
          width: fit-content;
        `
        badge.textContent = options.badge
        titleWrap.appendChild(badge)
      }

      header.appendChild(titleWrap)
      label.appendChild(header)
    }

    // Description (renders as paragraph like annotation notes)
    if (options.description) {
      const desc = document.createElement('p')
      desc.style.cssText = `
        margin: 0;
        font-size: 11px;
        color: #9ca3af;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      `
      desc.textContent = options.description
      label.appendChild(desc)
    }

    // Fields
    if (options.fields?.length) {
      const fields = document.createElement('div')
      fields.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:4px 12px;'

      options.fields.forEach((field) => {
        const item = document.createElement('div')
        item.style.cssText = 'display:flex;flex-direction:column;gap:1px;'

        const labelEl = document.createElement('span')
        labelEl.style.cssText =
          'font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:0.3px;'
        labelEl.textContent = field.label

        const value = document.createElement('span')
        value.style.cssText = 'font-size:11px;font-weight:500;color:#e4e4e4;'
        value.textContent = String(field.value)

        item.appendChild(labelEl)
        item.appendChild(value)
        fields.appendChild(item)
      })

      label.appendChild(fields)
    }

    // Footer
    if (options.footer) {
      const footer = document.createElement('div')
      footer.style.cssText =
        'margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.05);font-size:9px;color:#4b5563;'
      footer.textContent = options.footer
      label.appendChild(footer)
    }
  }

  /**
   * Add label with support for refs and callbacks
   */
  const add = (
    id: string,
    target: Cartesian3 | Entity | PositionGetter,
    options: LabelOptionsGetter,
    dependencies?: Array<Ref | (() => any)>, // Optional dependencies to watch
  ) => {
    if (!initSystem()) return null

    // Remove existing
    remove(id)

    // Determine position getter
    let position: Cartesian3 | (() => Cartesian3 | undefined)

    if (isRef(target)) {
      // Handle ref position
      position = () => unref(target) as Cartesian3 | undefined
    } else if (target instanceof Cartesian3) {
      position = target
    } else if ('position' in target && target.position) {
      // It's an Entity
      position = () => {
        if (!viewer.value) return undefined
        return target.position?.getValue(viewer.value.clock.currentTime)
      }
    } else {
      // It's already a position getter
      position = target as () => Cartesian3 | undefined
    }

    // Handle options - can be static, getter, or ref
    let optionsGetter: (() => LabelOptions) | undefined
    let initialOptions: LabelOptions

    if (isRef(options)) {
      // It's a ref - create getter that unwraps it
      optionsGetter = () => unref(options) as LabelOptions
      initialOptions = unref(options) as LabelOptions
    } else if (typeof options === 'function') {
      // It's already a getter
      optionsGetter = options as () => LabelOptions
      initialOptions = optionsGetter()
    } else {
      // It's static options
      initialOptions = options
      optionsGetter = undefined
    }

    // Create DOM
    const element = createLabelDOM(id, initialOptions)
    container?.appendChild(element)

    // Store label instance
    const labelInstance: LabelInstance = {
      element,
      position,
      optionsGetter,
      lastOptionsHash: optionsGetter ? JSON.stringify(initialOptions) : undefined,
      visible: true,
      maxDistance: initialOptions.maxDistance ?? DEFAULT_MAX_DISTANCE,
      watchers: [],
    }

    // Set up watchers for reactive updates
    if (isRef(options)) {
      // Watch the ref for changes
      const stopWatcher = watch(options as Ref<LabelOptions>, (newOptions) => {
        updateLabelDOM(element, newOptions)
        labelInstance.lastOptionsHash = JSON.stringify(newOptions)
      })
      labelInstance.watchers?.push(stopWatcher)
    }

    // Watch additional dependencies if provided
    if (dependencies?.length) {
      dependencies.forEach((dep) => {
        if (isRef(dep)) {
          const stopWatcher = watch(dep, () => {
            // Force update when dependency changes
            if (labelInstance.optionsGetter) {
              const newOptions = labelInstance.optionsGetter()
              updateLabelDOM(element, newOptions)
              labelInstance.lastOptionsHash = JSON.stringify(newOptions)
            }
          })
          labelInstance.watchers?.push(stopWatcher)
        }
      })
    }

    labels[id] = labelInstance

    return element
  }

  /**
   * Add label with automatic reactive tracking
   * Watches any refs used in the callback
   */
  const addReactive = (
    id: string,
    target: Cartesian3 | Entity | PositionGetter,
    optionsCallback: () => LabelOptions,
  ) => {
    if (!initSystem()) return null

    // Create a computed ref that tracks dependencies automatically
    const optionsRef = computed(() => optionsCallback())

    return add(id, target, optionsRef)
  }

  /**
   * Update label options
   */
  const update = (id: string, options: MaybeRef<LabelOptions>) => {
    const label = labels[id]
    if (!label) return

    const unwrappedOptions = unref(options)
    updateLabelDOM(label.element, unwrappedOptions)

    // If updating with a ref, set up new watcher
    if (isRef(options)) {
      // Clean up old watchers
      label.watchers?.forEach((stop) => stop())
      label.watchers = []

      // Set up new watcher
      const stopWatcher = watch(options, (newOptions) => {
        updateLabelDOM(label.element, newOptions)
        label.lastOptionsHash = JSON.stringify(newOptions)
      })
      label.watchers.push(stopWatcher)
      label.optionsGetter = () => unref(options) as LabelOptions
    } else {
      // Switch to static
      label.optionsGetter = undefined
      label.lastOptionsHash = undefined
    }
  }

  /**
   * Remove label
   */
  const remove = (id: string) => {
    const label = labels[id]
    if (!label) return

    // Clean up watchers
    label.watchers?.forEach((stop) => stop())

    label.element.remove()
    delete labels[id]
  }

  /**
   * Toggle visibility
   */
  const toggle = (id: string, visible?: boolean) => {
    const label = labels[id]
    if (!label) return

    label.visible = visible ?? !label.visible
    label.element.style.display = label.visible ? 'block' : 'none'
  }

  /**
   * Clean up everything
   */
  const cleanup = () => {
    Object.keys(labels).forEach(remove)

    updateListener?.()
    updateListener = null

    container?.remove()
    container = null
  }

  return {
    add,
    addReactive,
    update,
    remove,
    toggle,
    cleanup,
    labels,
  }
}
